/**
 * The roster's data: the selected plant and week from the store, the week's
 * rows (fetched once per plant × week by the roster slice), and the plant
 * filter that turns the company's rows into this plant's board.
 */
import { AnyAction, ThunkDispatch } from "@reduxjs/toolkit";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import { StoreType } from "../../../../utils/types";
import { buildPhaseOperationNames } from "../../utils/operation-names";
import { orderIdOf } from "../../utils/orders";
import { findStandardForOrder, isMachinePhase, placeOrderPhases, standardHasStepOn } from "../../utils/standard-matching";
import { localDay } from "../../utils/time";
import { KnownJob } from "../load-balance-board/ui/standing-crew-panel";
import { fetchRosterAvailability, fetchRosterWeek, refetchRosterAssignments, selectRosterWeek } from "../store/roster-slice";
import { RosterWeekKey } from "../store/roster-types";

/** A thunk-aware dispatch, so the slice's thunks can be awaited without `any`. */
export type RosterDispatch = ThunkDispatch<StoreType, unknown, AnyAction>;

export interface PlantWorkcenter {
	id: number;
	name: string;
	locationId: number;
}

interface StoreWorkcenter {
	id?: number;
	name: string;
	locationId?: number;
}

interface StoreLocation {
	id: number;
	name?: string;
}

interface StoreJob {
	id?: number | string | null;
	name?: string | null;
}

// ---- Typed selectors --------------------------------------------------------

/** The plant on screen; 0 until the filters have said which (nothing loads for a stand-in plant). */
const selectSelectedLocation = (state: StoreType): number =>
	Number(state.fullTimeShifts?.selectedLocation || state.supervisor?.selectedLocationId || 0);
const selectCurrentUserId = (state: StoreType): string | null | undefined => state.user?.userData?.id;
const NO_WORKCENTERS: StoreWorkcenter[] = [];
const NO_LOCATIONS: StoreLocation[] = [];
const NO_JOBS: StoreJob[] = [];
const selectAllWorkcenters = (state: StoreType): StoreWorkcenter[] =>
	(state.fullTimeShifts?.filterData?.allWorkcenters as StoreWorkcenter[] | undefined) ?? NO_WORKCENTERS;
const selectAllLocations = (state: StoreType): StoreLocation[] =>
	(state.fullTimeShifts?.filterData?.allLocations as StoreLocation[] | undefined) ?? NO_LOCATIONS;
const selectJobs = (state: StoreType): StoreJob[] =>
	(state.fullTimeShifts?.filterData?.jobs as StoreJob[] | undefined) ?? NO_JOBS;

// ---- Hook ----------------------------------------------------------------------

export interface UseRosterDataArgs {
	weekStartDate: Date;
	selectedDate: Date;
	/**
	 * Called after a reload that bypasses the board (the week fetch), so the
	 * board can drop its move-target cache too.
	 */
	onWeekLoaded?: () => void;
}

export function useRosterData({ weekStartDate, selectedDate, onWeekLoaded }: UseRosterDataArgs) {
	const dispatch = useDispatch<RosterDispatch>();
	const selectedLocation = useSelector(selectSelectedLocation);
	const currentUserId = useSelector(selectCurrentUserId);
	const allWorkcentersFromStore = useSelector(selectAllWorkcenters);
	const allLocationsFromStore = useSelector(selectAllLocations);
	const jobsFromStore = useSelector(selectJobs);

	const weekKey = useMemo<RosterWeekKey>(
		() => ({ locationId: selectedLocation, weekStart: localDay(weekStartDate) }),
		[selectedLocation, weekStartDate]
	);
	const week = useSelector((state: StoreType) => selectRosterWeek(state, weekKey));

	// The whole week, once per plant × week (and again after Allocate).
	const fetchWeek = useCallback(async () => {
		if (!currentUserId || !selectedLocation) {
			return;
		}
		// Who is out loads beside the week; the board shows as soon as the week is in.
		void dispatch(fetchRosterAvailability(weekKey));
		await dispatch(fetchRosterWeek(weekKey));
		onWeekLoaded?.();
	}, [dispatch, weekKey, currentUserId, selectedLocation, onWeekLoaded]);

	useEffect(() => {
		void fetchWeek();
	}, [fetchWeek]);

	// A week that failed to load (a 401 during a token refresh, a blip) retries
	// once a few seconds later instead of sitting empty until a reload.
	const retried = useRef<string | null>(null);
	useEffect(() => {
		if (week.weekStatus !== "error") {
			return undefined;
		}
		const id = `${weekKey.locationId}:${weekKey.weekStart}`;
		if (retried.current === id) {
			return undefined;
		}
		retried.current = id;
		const timer = window.setTimeout(() => void fetchWeek(), 3000);
		return () => window.clearTimeout(timer);
	}, [week.weekStatus, weekKey, fetchWeek]);

	// After a manual move / fill / unpin only the assignments (and who is out)
	// changed, so only they are refetched — the orders, standards and shifts on
	// screen are untouched and a full reload would flash the whole board.
	const refetchAssignments = useCallback(async () => {
		await dispatch(refetchRosterAssignments(weekKey));
	}, [dispatch, weekKey]);

	// The company's jobs, already loaded with the filters — the optional role
	// on a standing-crew pin.
	const knownJobs = useMemo<KnownJob[]>(
		() =>
			jobsFromStore
				.filter((job) => job && job.id != null && job.name)
				.map((job) => ({ id: Number(job.id), name: String(job.name) })),
		[jobsFromStore]
	);

	// The roster is one plant at a time: only the selected location's work
	// centers make rows and filter the orders (a multi-site company otherwise
	// showed every site's centers under one plant).
	const plantWorkcenters = useMemo<PlantWorkcenter[]>(() => {
		const scoped = allWorkcentersFromStore.filter((wc) => !selectedLocation || wc.locationId === selectedLocation);
		return (scoped.length ? scoped : allWorkcentersFromStore)
			.map((wc) => ({ id: wc.id || 0, name: wc.name, locationId: wc.locationId || 0 }))
			.sort((a, b) => a.name.localeCompare(b.name));
	}, [allWorkcentersFromStore, selectedLocation]);
	const companyWorkcenterNames = useMemo(() => new Set(plantWorkcenters.map((wc) => wc.name)), [plantWorkcenters]);
	// The lanes of the board's Line view, in display order.
	const plantWorkcenterNames = useMemo(() => plantWorkcenters.map((wc) => String(wc.name)), [plantWorkcenters]);

	// Only the orders on the plant's work centers.
	const manufacturingOrders = useMemo(
		() =>
			companyWorkcenterNames.size > 0
				? week.orders.filter((mo) => companyWorkcenterNames.has(mo.workstation))
				: week.orders,
		[week.orders, companyWorkcenterNames]
	);

	// A standard belongs to the plant when ANY of its steps runs on one of the
	// plant's work centers (the work center is per operation now).
	const laborStandards = useMemo(
		() =>
			companyWorkcenterNames.size > 0
				? week.standards.filter((ls) =>
					Array.from(companyWorkcenterNames).some((name) => standardHasStepOn(ls, String(name)))
				)
				: week.standards,
		[week.standards, companyWorkcenterNames]
	);

	// The roster is one plant at a time, and so are its assignments. An
	// assignment belongs to the plant when its ORDER is one of the plant's
	// (filtered) orders; only when the order is not in the fetched week at all
	// (a step that started last week) does its own work center decide — never
	// first, because "Dispensing" and "Sweco Sifter" exist at both plants.
	const plantAssignments = useMemo(() => {
		const plantOrderIds = new Set(manufacturingOrders.map((mo) => mo.id));
		const fetchedOrderIds = new Set(week.orders.map((mo) => mo.id));
		return week.assignments.filter((a) => {
			const orderId = orderIdOf(a);
			if (orderId !== undefined && plantOrderIds.has(orderId)) {
				return true;
			}
			if (orderId !== undefined && fetchedOrderIds.has(orderId)) {
				return false;
			}
			return companyWorkcenterNames.size === 0 || (Boolean(a.workstation) && companyWorkcenterNames.has(a.workstation as string));
		});
	}, [week.assignments, manufacturingOrders, week.orders, companyWorkcenterNames]);

	// Labor the selected day's orders demand, for the board's coverage metric:
	// worker-hours (crew x duration) of every placed phase that falls on the
	// day — not the standard's total duration, which ignores crew size and
	// counts a whole recipe against a single step.
	const dayRequiredHours = useMemo(() => {
		const dayStart = new Date(selectedDate);
		dayStart.setHours(0, 0, 0, 0);
		const dayEnd = dayStart.getTime() + 86400000;
		return manufacturingOrders.reduce((total, mo) => {
			const standard = findStandardForOrder(mo, laborStandards);
			if (!standard) {
				return total;
			}
			return (
				total +
				placeOrderPhases(mo, standard).reduce((hours, placed) => {
					// Machine phases hold the unit but need nobody.
					if (isMachinePhase(placed.phase)) {
						return hours;
					}
					const start = Math.max(placed.start.getTime(), dayStart.getTime());
					const end = Math.min(placed.end.getTime(), dayEnd);
					if (end <= start) {
						return hours;
					}
					return hours + ((end - start) / 3600000) * placed.phase.workersRequired;
				}, 0)
			);
		}, 0);
	}, [manufacturingOrders, laborStandards, selectedDate]);

	// Phase id -> operation name, so the board's blocks read "Bed cap" rather
	// than the job role.
	const boardOperationNames = useMemo(() => buildPhaseOperationNames(laborStandards), [laborStandards]);

	const locationName = useMemo(
		() => allLocationsFromStore.find((loc) => loc.id === selectedLocation)?.name,
		[allLocationsFromStore, selectedLocation]
	);

	return {
		selectedLocation,
		currentUserId,
		locationName,
		workcenters: plantWorkcenters,
		plantWorkcenterNames,
		knownJobs,
		manufacturingOrders,
		laborStandards,
		plantAssignments,
		/** Every block of the week at any plant: who is already busy at a sister plant. */
		allAssignments: week.assignments,
		shifts: week.shifts,
		availability: week.availability,
		dayRequiredHours,
		boardOperationNames,
		loading: week.weekStatus === "pending",
		fetchWeek,
		refetchAssignments,
	};
}

export type RosterData = ReturnType<typeof useRosterData>;
